import { useCallback, useState } from 'react';

function getStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function cloneFilters(filters) {
  try {
    return JSON.parse(JSON.stringify(filters || {}));
  } catch {
    return {};
  }
}

function createPresetId() {
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function readFilterPresets(storageKey) {
  const storage = getStorage();
  if (!storage || !storageKey) {
    return [];
  }

  try {
    const raw = storage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((preset) => ({
        id: String(preset?.id || createPresetId()),
        name: String(preset?.name || '').trim(),
        filters: cloneFilters(preset?.filters),
      }))
      .filter((preset) => preset.name);
  } catch {
    return [];
  }
}

export function writeFilterPresets(storageKey, presets) {
  const storage = getStorage();
  if (!storage || !storageKey) {
    return;
  }

  try {
    storage.setItem(storageKey, JSON.stringify(Array.isArray(presets) ? presets : []));
  } catch {
    // localStorage can be unavailable in private/restricted browser modes.
  }
}

export function useFilterPresets({ storageKey, filters, onApply }) {
  const [presets, setPresets] = useState(() => readFilterPresets(storageKey));
  const [isFilterPresetModalOpen, setIsFilterPresetModalOpen] = useState(false);
  const [filterPresetName, setFilterPresetName] = useState('');
  const [filterPresetNameError, setFilterPresetNameError] = useState('');

  const openSaveFilterPreset = useCallback(() => {
    setFilterPresetName('');
    setFilterPresetNameError('');
    setIsFilterPresetModalOpen(true);
  }, []);

  const closeSaveFilterPreset = useCallback(() => {
    setIsFilterPresetModalOpen(false);
    setFilterPresetName('');
    setFilterPresetNameError('');
  }, []);

  const saveFilterPreset = useCallback((event) => {
    event?.preventDefault?.();

    const name = String(filterPresetName || '').trim();
    if (!name) {
      setFilterPresetNameError('filterPresets.nameRequired');
      return false;
    }

    setPresets((currentPresets) => {
      const normalizedName = name.toLowerCase();
      const withoutDuplicate = currentPresets.filter(
        (preset) => preset.name.toLowerCase() !== normalizedName,
      );
      const nextPresets = [
        ...withoutDuplicate,
        {
          id: createPresetId(),
          name,
          filters: cloneFilters(filters),
        },
      ];

      writeFilterPresets(storageKey, nextPresets);
      return nextPresets;
    });

    closeSaveFilterPreset();
    return true;
  }, [closeSaveFilterPreset, filterPresetName, filters, storageKey]);

  const applyFilterPreset = useCallback((presetId) => {
    const preset = presets.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    onApply?.(cloneFilters(preset.filters));
  }, [onApply, presets]);

  const deleteFilterPreset = useCallback((presetId) => {
    const normalizedPresetId = String(presetId || '').trim();
    if (!normalizedPresetId) {
      return;
    }

    setPresets((currentPresets) => {
      const nextPresets = currentPresets.filter((preset) => preset.id !== normalizedPresetId);
      writeFilterPresets(storageKey, nextPresets);
      return nextPresets;
    });
  }, [storageKey]);

  return {
    filterPresets: presets,
    isFilterPresetModalOpen,
    filterPresetName,
    filterPresetNameError,
    openSaveFilterPreset,
    closeSaveFilterPreset,
    setFilterPresetName,
    saveFilterPreset,
    applyFilterPreset,
    deleteFilterPreset,
  };
}
